import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserX, Download, Phone, AlertCircle } from 'lucide-react';
import { getInactive, InactiveResp, InactiveRow } from '../api';
import { getWhatsAppLink } from '../../../utils/whatsapp';
import { Skeleton } from '../../../components/Skeleton';
import { readCache, writeCache } from '../../../services/localCache';
import CountUp from '../../../components/CountUp';
import EmptyState from '../../../components/EmptyState';

const DAY_OPTIONS = [30, 60, 90];

function fullName(r: InactiveRow) { return `${r.firstname || ''} ${r.lastname || ''}`.trim(); }
function fmtDate(d: string | null) {
  if (!d) return '—';
  return new Date(d.slice(0, 10) + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
}
function waLink(r: InactiveRow) {
  const msg = `Hola ${r.firstname || ''} 💖 Te extrañamos en Let's Move. ¿Te gustaría regresar a clase?`;
  return getWhatsAppLink(r.phone_prefix, r.phone_number, msg) || null;
}

function exportCsv(r: InactiveResp) {
  const lines = ['Alumno,Teléfono,Mensualidad,Último pago,Días sin pagar,Pagos'];
  for (const x of r.data) {
    lines.push([
      `"${fullName(x)}"`, `${x.phone_prefix || ''}${x.phone_number || ''}`,
      `"${x.membership_name || ''}"`, x.last_payment ? x.last_payment.slice(0, 10) : 'NUNCA',
      x.days_since ?? '', x.payments_count,
    ].join(','));
  }
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `alumnos-inactivos-${r.days}d.csv`; a.click();
  URL.revokeObjectURL(url);
}

export default function InactiveReport() {
  // Pinta al instante lo último conocido por rango (caché local) y refresca por detrás.
  const [data, setData] = useState<InactiveResp | null>(() => readCache<InactiveResp>('rep-inactive:60') ?? null);
  const [days, setDays] = useState(60);
  const [loading, setLoading] = useState(() => !readCache('rep-inactive:60'));

  useEffect(() => {
    const cached = readCache<InactiveResp>(`rep-inactive:${days}`);
    if (cached) { setData(cached); setLoading(false); } else setLoading(true);
    getInactive(days).then((r) => { setData(r); writeCache(`rep-inactive:${days}`, r); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [days]);

  const pct = useMemo(
    () => (data && data.summary.active_students ? Math.round((data.summary.inactive / data.summary.active_students) * 100) : 0),
    [data]
  );

  return (
    <div className="space-y-4">
      {/* Controles */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-content-3">
          <span>Sin pago en más de</span>
          <div className="flex rounded-lg border border-line bg-surface p-0.5">
            {DAY_OPTIONS.map((d) => (
              <button key={d} onClick={() => setDays(d)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                  d === days ? 'bg-accent text-on-accent' : 'text-content-2 hover:bg-surface-2'}`}>
                {d} días
              </button>
            ))}
          </div>
        </div>
        {data && data.data.length > 0 && (
          <button onClick={() => exportCsv(data)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 hover:bg-surface-2">
            <Download size={15} /> CSV
          </button>
        )}
      </div>

      {loading || !data ? (
        <Skeleton className="h-96 w-full rounded-2xl" />
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Inactivos</div>
              <div className="mt-1 font-display text-2xl font-bold text-content"><CountUp value={data.summary.inactive} /></div>
              <div className="mt-1 text-xs text-content-3">{pct}% de {data.summary.active_students} activos</div>
            </div>
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Con pago previo</div>
              <div className="mt-1 font-display text-2xl font-bold text-content"><CountUp value={data.summary.with_old_payment} /></div>
              <div className="mt-1 text-xs text-content-3">dejaron de pagar</div>
            </div>
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Sin pago registrado</div>
              <div className="mt-1 font-display text-2xl font-bold text-content"><CountUp value={data.summary.never} /></div>
              <div className="mt-1 text-xs text-content-3">nunca, o pago no ligado</div>
            </div>
          </div>

          {data.data.length === 0 ? (
            <EmptyState icon={UserX} title="Ningún alumno inactivo" subtitle={`Todos pagaron en los últimos ${days} días.`} />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
              {/* Tabla (escritorio) */}
              <table className="hidden w-full text-sm sm:table">
                <thead>
                  <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-content-3">
                    <th className="px-4 py-3 font-medium">Alumno</th>
                    <th className="px-4 py-3 font-medium">Mensualidad</th>
                    <th className="px-4 py-3 font-medium">Último pago</th>
                    <th className="px-4 py-3 text-right font-medium">Sin pagar</th>
                    <th className="px-4 py-3 text-center font-medium">Contacto</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((r) => {
                    const wa = waLink(r);
                    return (
                      <tr key={r.account_id} className="border-b border-line/60 last:border-0 even:bg-surface-2/40 hover:bg-surface-2/70">
                        <td className="px-4 py-2.5">
                          <Link to={`/alumnos/${r.account_id}`} className="flex items-center gap-2.5 hover:underline">
                            {r.has_photo ? (
                              <img src={`/api/letsmove/members/${r.account_id}/photo`} alt="" className="h-8 w-8 rounded-full object-cover" loading="lazy" />
                            ) : (
                              <span className="grid h-8 w-8 place-items-center rounded-full bg-surface-2 text-xs font-medium text-content-3">
                                {(r.firstname?.[0] || '?').toUpperCase()}
                              </span>
                            )}
                            <span className="font-medium text-content">{fullName(r)}</span>
                          </Link>
                        </td>
                        <td className="px-4 py-2.5 text-content-2">{r.membership_name || '—'}</td>
                        <td className="px-4 py-2.5 text-content-2">
                          {r.last_payment ? fmtDate(r.last_payment) : (
                            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                              <AlertCircle size={13} /> nunca
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {r.days_since !== null ? (
                            <span className={`font-medium ${r.days_since > 120 ? 'text-red-600 dark:text-red-400' : 'text-content'}`}>
                              {r.days_since} días
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          {wa ? (
                            <a href={wa} target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-xs font-medium text-emerald-600 hover:bg-surface-2 dark:text-emerald-400"
                              title="Escribir por WhatsApp">
                              <Phone size={12} /> WhatsApp
                            </a>
                          ) : <span className="text-xs text-content-3">sin tel.</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Tarjetas (móvil) */}
              <ul className="divide-y divide-line/60 sm:hidden">
                {data.data.map((r) => {
                  const wa = waLink(r);
                  return (
                    <li key={r.account_id} className="p-3">
                      <div className="flex items-start justify-between gap-3">
                        <Link to={`/alumnos/${r.account_id}`} className="flex min-w-0 items-center gap-2.5">
                          {r.has_photo ? (
                            <img src={`/api/letsmove/members/${r.account_id}/photo`} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" loading="lazy" />
                          ) : (
                            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-surface-2 text-xs font-medium text-content-3">
                              {(r.firstname?.[0] || '?').toUpperCase()}
                            </span>
                          )}
                          <span className="min-w-0">
                            <span className="block truncate font-medium text-content">{fullName(r)}</span>
                            <span className="block truncate text-xs text-content-3">{r.membership_name || '—'}</span>
                          </span>
                        </Link>
                        {r.days_since !== null && (
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${r.days_since > 120 ? 'bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-surface-2 text-content-2'}`}>
                            {r.days_since} días
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="text-xs text-content-3">
                          Último pago: {r.last_payment ? fmtDate(r.last_payment) : (
                            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"><AlertCircle size={12} /> nunca</span>
                          )}
                        </span>
                        {wa ? (
                          <a href={wa} target="_blank" rel="noreferrer"
                            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-line px-2 py-1 text-xs font-medium text-emerald-600 hover:bg-surface-2 dark:text-emerald-400">
                            <Phone size={12} /> WhatsApp
                          </a>
                        ) : <span className="shrink-0 text-xs text-content-3">sin tel.</span>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
